// Календарь датасета «Скаута»: сетка месяца, оттенок дня, шаг по снимкам дня.
//
// Сутки — UTC, как у сервера (`src/cabinet/imagery_calendar.py`): дата снимка
// у поставщика названа в UTC, и местные сутки разнесли бы ночной пролёт на
// соседний день.

import type { DatasetSensor } from './imageryUrl'

export interface CalendarDay {
  date: string
  optical: number
  radar: number
  /** Счётчики записей — только у календаря сверки с АИС; у пролётов их нет. */
  records?: number
  identified?: number
  found?: number
  not_found?: number
  excluded?: number
  /** Отметок без АИС за день. */
  no_ais?: number
}

export type DayTone = 'none' | 'optical' | 'radar' | 'both'

/** Сетка месяца неделями, понедельник первым; клетки вне месяца — `null`. */
export function monthGrid(month: string): (string | null)[][] {
  const [y, m] = month.split('-').map(Number)
  const first = new Date(Date.UTC(y, m - 1, 1))
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const lead = (first.getUTCDay() + 6) % 7
  const cells: (string | null)[] = Array(lead).fill(null)
  for (let d = 1; d <= days; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`)
  while (cells.length % 7) cells.push(null)
  const weeks: (string | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/** Оттенок дня. Пустой день не красится: «снимков нет» — это не цвет. */
export function dayTone(day: CalendarDay | undefined): DayTone {
  if (!day) return 'none'
  if (day.optical > 0 && day.radar > 0) return 'both'
  if (day.optical > 0) return 'optical'
  if (day.radar > 0) return 'radar'
  return 'none'
}

/** Месяц календаря: названный в ссылке, иначе последний со снимками. */
export function pickMonth(months: string[], named: string | null): string | null {
  if (named) return named
  return months.length ? months[months.length - 1] : null
}

export interface SceneLike {
  source: string
  scene_id: string
  sensor: string
}

export function sceneKey(scene: { source: string; scene_id: string }): string {
  return `${scene.source}:${scene.scene_id}`
}

export function scenesOfSensor<T extends SceneLike>(scenes: T[], sensor: DatasetSensor): T[] {
  return sensor === 'all' ? scenes : scenes.filter(s => s.sensor === sensor)
}

/** Соседний снимок по кругу; без выбора — первый. */
export function stepScene(scenes: SceneLike[], current: string | null, delta: number): string | null {
  if (scenes.length === 0) return null
  const i = scenes.findIndex(s => sceneKey(s) === current)
  if (i < 0) return sceneKey(scenes[0])
  const n = scenes.length
  return sceneKey(scenes[(((i + delta) % n) + n) % n])
}

export interface ListedScene extends SceneLike {
  captured_at: string
  /** Записи по статусам; у пролёта без сверки с АИС поля нет. */
  records?: Record<string, number>
  /** Отметок без АИС на снимке; нет поля — сервер их ещё не отдаёт. */
  marks?: number
}

/** Набор, который разбирают: все записи, пропуски прибора или отметки без АИС. */
export type DatasetSet = 'all' | 'missed' | 'noais'

export function recordCount(scene: { records?: Record<string, number> }): number {
  return Object.values(scene.records ?? {}).reduce((a, b) => a + b, 0)
}

/** Сколько у снимка элементов набора: записей, пропусков или отметок без АИС. */
export function setCount(scene: ListedScene, set: DatasetSet): number {
  if (set === 'missed') return scene.records?.not_found ?? 0
  if (set === 'noais') return scene.marks ?? 0
  return recordCount(scene)
}

export interface ListOptions {
  sensor: DatasetSensor
  set: DatasetSet
  /** Набор «все»: показывать и снимки без записей. */
  showEmpty: boolean
  /** Выбранный по ссылке снимок остаётся в списке. */
  keep: string | null
}

/** Снимки дня в списке: больше элементов набора — выше, равные — по времени.
 *
 * Снимок без записей — суша, лёд или вода без единого фикса АИС; таких за сутки
 * сотни, и по умолчанию они скрыты. В наборах «пропуски» и «без АИС» остаются
 * только снимки, где элементы набора есть. Выбранный по ссылке остаётся в
 * списке: пропав из него, он снял бы и картинку с карты. */
export function listScenes<T extends ListedScene>(scenes: T[], o: ListOptions): T[] {
  const n = (s: T) => setCount(s, o.set)
  return scenesOfSensor(scenes, o.sensor)
    .filter(s => n(s) > 0 || (o.set === 'all' && o.showEmpty) || sceneKey(s) === o.keep)
    .sort((a, b) => n(b) - n(a) || a.captured_at.localeCompare(b.captured_at))
}

/** Элемент набора на карте: запись (пропуск) или отметка без АИС. */
export interface SetItem {
  kind: 'record' | 'mark'
  id: number
  scene: string
  lng: number
  lat: number
}

interface ItemRecord { record_id: number; source: string; scene_id: string; status: string; lat: number; lon: number }
interface ItemMark { mark_id: number; source: string; scene_id: string; lat: number; lon: number }

/** Элементы набора в порядке списка снимков — по ним листают ‹ ›. Набор «все»
 *  элементов не даёт: листать там нечего, кроме снимков. */
export function setItems(scenes: SceneLike[], records: ItemRecord[], marks: ItemMark[],
                         set: DatasetSet): SetItem[] {
  if (set === 'all') return []
  const order = new Map(scenes.map((s, i) => [sceneKey(s), i]))
  const items: SetItem[] = set === 'missed'
    ? records.filter(r => r.status === 'not_found')
      .map(r => ({ kind: 'record', id: r.record_id, scene: sceneKey(r), lng: r.lon, lat: r.lat }))
    : marks.map(m => ({ kind: 'mark', id: m.mark_id, scene: sceneKey(m), lng: m.lon, lat: m.lat }))
  return items
    .filter(it => order.has(it.scene))
    .sort((a, b) => order.get(a.scene)! - order.get(b.scene)! || a.id - b.id)
}

/** Соседний элемент по кругу; без выбора — первый. */
export function stepItem(items: SetItem[], current: { kind: string; id: number } | null,
                         delta: number): SetItem | null {
  if (items.length === 0) return null
  const i = current ? items.findIndex(x => x.kind === current.kind && x.id === current.id) : -1
  if (i < 0) return items[0]
  const n = items.length
  return items[(((i + delta) % n) + n) % n]
}
